import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { connect } from "react-redux";
import { formatCurrency } from "../../../../helpers/general";
import "./Simulacao.scss";

const Form = ({ ...props }) => {
  // Valores padrão caso não sejam passados nas props
  const limitesDefault = {
    prazo: { minimo: 1, maximo: 36, padrao: null },
    prestacao: { minimo: 20, maximo: 1919.682, padrao: null },
    contratacao: { minimo: 50, maximo: 700000, padrao: null },
    creditoCliente: { minimo: 50, maximo: 500000, padrao: null },
    carencia: { minimo: null, maximo: null, padrao: null },
    juros: { minimo: 3.63, maximo: 4, padrao: 3.6663 },
  };

  const [tipoSimulacao, setTipoSimulacao] = useState("MAXIMO");
  const [prazo, setPrazo] = useState("");
  const [valor, setValor] = useState("");
  const [parcela, setParcela] = useState("");
  const [errors, setErrors] = useState({});

  const getLimite = (tipo) => {
    // Se limites foi passado como objeto (não array)
    if (props.limites && typeof props.limites === 'object' && !Array.isArray(props.limites)) {
      const chave = tipo.toLowerCase();
      return props.limites[chave] || null;
    }

    // Se limites foi passado como array
    if (props.limites && Array.isArray(props.limites) && props.limites.length > 0) {
      return props.limites.find((limite) => limite.tipo === tipo);
    }

    // Retorna valores padrão se não houver limites nas props
    const chave = tipo.toLowerCase();
    return limitesDefault[chave] || null;
  };

  const limitesPrazo = useMemo(() => getLimite("prazo"), [props.limites]);
  const limitesContratacao = useMemo(() => getLimite("contratacao"), [props.limites]);
  const limitesParcela = useMemo(() => getLimite("prestacao"), [props.limites]);
  const limitesCreditoCliente = useMemo(() => getLimite("creditoCliente"), [props.limites]);
  const limitesJuros = useMemo(() => getLimite("juros"), [props.limites]);

  useEffect(() => {
    if (tipoSimulacao === "MAXIMO") {
      // Setar parcela máxima e prazo máximo
      setValor("");
      if (limitesParcela?.maximo) {
        const parcelaMaxima = limitesParcela.maximo.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        setParcela(parcelaMaxima);
      }
      if (limitesPrazo?.maximo) {
        setPrazo(limitesPrazo.maximo.toString());
      }
    } else if (tipoSimulacao === "VALOR") {
      setParcela("");
      // Setar valor máximo ao selecionar o card
      if (limitesCreditoCliente?.maximo) {
        const valorMaximo = limitesCreditoCliente.maximo.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        setValor(valorMaximo);
      }
    } else {
      setValor("");
      // Setar valor máximo ao selecionar o card
      if (limitesParcela?.maximo) {
        const valorMaximo = limitesParcela.maximo.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        setParcela(valorMaximo);
      }
    }
    setErrors({});
  }, [tipoSimulacao, limitesParcela, limitesPrazo, limitesCreditoCliente]);

  function goBack() {
    if (props.onCloseEv) props.onCloseEv();
  }

  function limparCampos() {
    setPrazo("");
    setValor("");
    setParcela("");
    setErrors({});
    setTipoSimulacao("MAXIMO");
  }

  function validarCampos() {
    const newErrors = {};

    if (!prazo || prazo === "") {
      newErrors.prazo = "Campo obrigatório";
    } else if (limitesPrazo) {
      const prazoNum = parseInt(prazo);
      if (prazoNum < limitesPrazo.minimo || prazoNum > limitesPrazo.maximo) {
        newErrors.prazo = `Prazo deve estar entre ${limitesPrazo.minimo} e ${limitesPrazo.maximo} meses`;
      }
    }

    if (tipoSimulacao === "VALOR") {
      if (!valor || valor === "") {
        newErrors.valor = "Campo obrigatório";
      } else {
        const valorNum = parseFloat(valor.replace(/\./g, "").replace(",", "."));
        const limiteMin = limitesCreditoCliente?.minimo || limitesContratacao?.minimo;
        const limiteMax = limitesCreditoCliente?.maximo || limitesContratacao?.maximo;

        if (limiteMin && limiteMax) {
          if (valorNum < limiteMin || valorNum > limiteMax) {
            newErrors.valor = `Valor deve estar entre ${formatCurrency(limiteMin)} e ${formatCurrency(limiteMax)}`;
          }
        }
      }
    } else {
      // Para PARCELA ou MAXIMO, validar parcela
      if (!parcela || parcela === "") {
        newErrors.parcela = "Campo obrigatório";
      } else if (limitesParcela) {
        const parcelaNum = parseFloat(parcela.replace(/\./g, "").replace(",", "."));
        if (parcelaNum < limitesParcela.minimo || parcelaNum > limitesParcela.maximo) {
          newErrors.parcela = `Parcela deve estar entre ${formatCurrency(limitesParcela.minimo)} e ${formatCurrency(limitesParcela.maximo)}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function simular() {
    if (!validarCampos()) {
      return;
    }

    // MAXIMO é tratado como PARCELA para o backend
    const tipoParaEnvio = tipoSimulacao === "MAXIMO" ? "PARCELA" : tipoSimulacao;

    const dados = {
      tipo: tipoParaEnvio,
      prazo: parseInt(prazo),
      valor: tipoSimulacao === "VALOR" ? parseFloat(valor.replace(/\./g, "").replace(",", ".")) : null,
      parcela: (tipoSimulacao === "PARCELA" || tipoSimulacao === "MAXIMO") ? parseFloat(parcela.replace(/\./g, "").replace(",", ".")) : null,
      juros: limitesJuros?.padrao || limitesJuros?.minimo,
    };

    if (props.simularContratoCTrab) {
      props.simularContratoCTrab(dados);
    }
  }

  function formatarMoeda(value) {
    if (!value) return "";
    const numero = value.replace(/\D/g, "");
    const valorNumerico = parseFloat(numero) / 100;
    return valorNumerico.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function handleValorChange(e) {
    const valorFormatado = formatarMoeda(e.target.value);
    setValor(valorFormatado);
    if (errors.valor) {
      setErrors({ ...errors, valor: null });
    }
  }

  function handleParcelaChange(e) {
    const valorFormatado = formatarMoeda(e.target.value);
    setParcela(valorFormatado);
    if (errors.parcela) {
      setErrors({ ...errors, parcela: null });
    }
  }

  function handlePrazoChange(e) {
    const value = e.target.value.replace(/\D/g, "");
    setPrazo(value);
    if (errors.prazo) {
      setErrors({ ...errors, prazo: null });
    }
  }

  function ModalBody() {
    return (
      <div className="simulador-financeiro">
        {/* Header */}
        <div className="simulador-financeiro-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="header-title">Simulação de Crédito Trabalhador</h3>
        </div>

        {/* Tipo de Simulação */}
        <div className="simulador-section">
          <div className="section-header">
            <span className="section-title">Tipo de Simulação</span>
          </div>
          <div className="tipo-simulacao-cards">
            {/* Card Valores Máximos */}
            <div
              className={`tipo-card tipo-card-featured ${tipoSimulacao === "MAXIMO" ? "active" : ""}`}
              onClick={() => setTipoSimulacao("MAXIMO")}
            >
              <div className="card-badge">Recomendado</div>
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="card-content">
                <h4 className="card-title">Valores Máximos</h4>
                <p className="card-description">
                  Simular pelos valores máximos permitidos
                </p>
              </div>
              <div className="card-check">
                {tipoSimulacao === "MAXIMO" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </div>

            <div
              className="tipo-card disabled"
            >
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="card-content">
                <h4 className="card-title">Por Valor</h4>
                <p className="card-description">Simular por Valor Desejado</p>
              </div>
              <div className="card-check">
                {tipoSimulacao === "VALOR" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </div>

            <div
              className="tipo-card disabled"
            >
              <div className="card-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7 15H7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11 15H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="card-content">
                <h4 className="card-title">Por Parcela</h4>
                <p className="card-description">Simular por Valor de Parcela</p>
              </div>
              <div className="card-check">
                {tipoSimulacao === "PARCELA" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Formulário */}
        {tipoSimulacao !== "MAXIMO" && (
          <div className="simulador-section">
            <div className="form-grid">
              {/* Valor ou Parcela */}
              {tipoSimulacao === "VALOR" ? (
                <div className="form-field">
                  <label className="field-label">
                    Valor Desejado
                    {limitesCreditoCliente && (
                      <span className="field-range">
                        {formatCurrency(limitesCreditoCliente.minimo)} - {formatCurrency(limitesCreditoCliente.maximo)}
                      </span>
                    )}
                  </label>
                  <div className={`field-input-wrapper ${errors.valor ? "error" : ""}`}>
                    <span className="input-icon">R$</span>
                    <input
                      type="text"
                      className="field-input"
                      value={valor}
                      onChange={handleValorChange}
                      placeholder="0,00"
                      autoFocus
                    />
                  </div>
                  {errors.valor && <span className="field-error">{errors.valor}</span>}
                </div>
              ) : (
                <div className="form-field">
                  <label className="field-label">
                    Valor da Parcela
                  </label>
                  <div className={`field-input-wrapper ${errors.parcela ? "error" : ""}`}>
                    <span className="input-icon">R$</span>
                    <input
                      type="text"
                      className="field-input"
                      value={parcela}
                      onChange={handleParcelaChange}
                      placeholder="0,00"
                    />
                  </div>
                  {errors.parcela && <span className="field-error">{errors.parcela}</span>}
                </div>
              )}

              {/* Prazo */}
              <div className="form-field">
                <label className="field-label">
                  Prazo {limitesPrazo && (
                    <span className="field-range-inline">
                      ({limitesPrazo.minimo} - {limitesPrazo.maximo} meses)
                    </span>
                  )}
                </label>
                <div className={`field-input-wrapper ${errors.prazo ? "error" : ""}`}>
                  <input
                    type="text"
                    className="field-input"
                    value={prazo}
                    onChange={handlePrazoChange}
                    placeholder="36"
                  />
                  <span className="input-suffix">meses</span>
                </div>
                {errors.prazo && <span className="field-error">{errors.prazo}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="simulador-actions">
          <button className="btn-action btn-secondary" onClick={limparCampos}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Limpar
          </button>
          <button className="btn-action btn-primary" onClick={simular}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.76489 14.1003 1.98232 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Simular
          </button>
        </div>

        <button className="btn-link" onClick={goBack}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Voltar
        </button>
      </div>
    );
  }

  return (
    <Modal
      size="lg"
      show={true}
      onHide={() => props.onCloseEv()}
      centered
      animation={false}
      backdrop="static"
      className="modal-simulador-financeiro"
    >
      <Modal.Body>
        <ModalBody />
      </Modal.Body>
    </Modal>
  );
};

function mapStateToProps(state) {
  return {
    detalhes: state.dados.detalhes,
    proposta: state.dados.proposta,
  };
}

export default connect(mapStateToProps)(Form);
